import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";

interface CareerItem {
  date: string;
  startDate: string;
  endDate: string | null;
  title: string;
  content: string;
  id?: string;
}

/** YYYY/MM を比較用の数値に変換。null (現在) は無限大として扱う */
function toNum(d: string | null | undefined): number {
  if (!d) return 999999;
  const [y, m] = d.split("/").map(Number);
  return y * 100 + m;
}

/**
 * アイテムを最大 2 レーンに割り当てる。
 * 終了月と開始月が同月でも重複とみなし別レーンへ振り分ける（厳格判定: <）。
 * 両レーンが占有中の場合はレーン 1 にスタック。
 * 戻り値はそれぞれ新しい順（表示用）にソートされた [lane0, lane1]。
 */
function assignLanes(items: CareerItem[]): [CareerItem[], CareerItem[]] {
  const sorted = [...items].sort(
    (a, b) => toNum(a.startDate) - toNum(b.startDate)
  );

  const lanes: CareerItem[][] = [[], []];
  const laneEnds: number[] = [0, 0];

  for (const item of sorted) {
    const start = toNum(item.startDate);
    const end = toNum(item.endDate);

    // 厳格判定: laneEnd < start のときのみ空き（同月は重複とみなす）
    if (laneEnds[0] < start) {
      lanes[0].push(item);
      laneEnds[0] = end;
    } else if (laneEnds[1] < start) {
      lanes[1].push(item);
      laneEnds[1] = end;
    } else {
      // 両レーン占有中 → レーン 1 にスタック
      lanes[1].push(item);
      laneEnds[1] = Math.max(laneEnds[1], end);
    }
  }

  // 新しい順（下から上へ時系列 = 画面上部が最新）
  for (const lane of lanes) {
    lane.sort((a, b) => toNum(b.startDate) - toNum(a.startDate));
  }

  return [lanes[0], lanes[1]];
}

function TimelineColumn({ items }: { items: CareerItem[] }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {items.map((item, i) => (
        <Box
          key={`${item.startDate}-${item.title}`}
          sx={{ display: "flex", gap: 0, position: "relative" }}
        >
          {/* タイムライン: ドット + 縦線 */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mr: 3,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: "#3B82F6",
                border: "2px solid #BFDBFE",
                flexShrink: 0,
                mt: "6px",
              }}
            />
            {i < items.length - 1 && (
              <Box
                sx={{
                  width: 2,
                  flex: 1,
                  minHeight: 40,
                  backgroundColor: "#E2E8F0",
                  my: 0.5,
                }}
              />
            )}
          </Box>

          {/* カード */}
          <Box
            id={item.id || undefined}
            sx={{
              flex: 1,
              backgroundColor: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: 2,
              p: 3,
              mb: i < items.length - 1 ? 2 : 0,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              transition: "box-shadow 0.2s ease",
              "&:hover": {
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#3B82F6",
                fontWeight: 600,
                fontSize: "0.75rem",
                letterSpacing: "0.05em",
                display: "block",
                mb: 0.5,
              }}
            >
              {item.date}
            </Typography>
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 700, color: "#0F172A", fontSize: "1rem", mb: 1 }}
            >
              {item.title}
            </Typography>
            <Typography
              variant="body2"
              component="div"
              sx={{ color: "#64748B", lineHeight: 1.8, fontSize: "0.875rem" }}
              dangerouslySetInnerHTML={{ __html: item.content }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function Career() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const careerItems = t("bio.career", { returnObjects: true }) as CareerItem[];
  const [lane0, lane1] = assignLanes(careerItems);

  // モバイルは全アイテムを新しい順で1列表示
  const mergedItems = [...careerItems].sort(
    (a, b) => toNum(b.startDate) - toNum(a.startDate)
  );

  if (isMobile) {
    return (
      <Box sx={{ maxWidth: 680, margin: "0 auto" }}>
        <TimelineColumn items={mergedItems} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: lane1.length > 0 ? 1200 : 680,
        margin: "0 auto",
        display: "flex",
        gap: 5,
        alignItems: "flex-start",
      }}
    >
      <TimelineColumn items={lane0} />
      {lane1.length > 0 && <TimelineColumn items={lane1} />}
    </Box>
  );
}
